'use client';

import { useMemo, useState, createContext, useEffect } from 'react';
import type {
  OverviewProviderProps,
  QuestionnaireTablePagination,
} from './OverviewContext.types';
import type {
  OverviewFilterObject,
  OverviewColumnType,
} from '@/_types/overview';
import { areSameObjects } from '@/_utils/objects';
import {
  DEFAULT_OVERVIEW_FILTER,
  DEFAULT_OVERVIEW_COLUMNS,
} from '@/_constants/overview';
import {
  setCookie,
  getCookie,
  deleteCookie,
  CookieValueTypes,
} from 'cookies-next/client';
import {
  OVERVIEW_FILTERS_COOKIE,
  OVERVIEW_COLUMNS_COOKIE,
  OVERVIEW_PAGE_SIZE_COOKIE,
  MAX_COOKIE_AGE,
} from '@/_constants/cookies';
import { SortingState } from '@tanstack/react-table';

const DEFAULT_PAGINATION_STATE: QuestionnaireTablePagination = {
  pageIndex: 0,
  pageSize: DEFAULT_OVERVIEW_FILTER.rows_per_page,
};

const OverviewContext = createContext<OverviewProviderProps>({
  columns: DEFAULT_OVERVIEW_COLUMNS,
  filter: DEFAULT_OVERVIEW_FILTER,
  isFiltering: false,
  pagination: DEFAULT_PAGINATION_STATE,
  resetColumns: () => null,
  resetFilters: () => null,
  setColumns: () => null,
  setFilter: () => null,
  setPagination: () => null,
  setSorting: () => null,
  sorting: [],
});

const OverviewProvider = (props: any) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filter, setFilter] = useState<OverviewFilterObject>(
    DEFAULT_OVERVIEW_FILTER,
  );
  const [columns, setColumns] = useState<Array<OverviewColumnType>>(
    DEFAULT_OVERVIEW_COLUMNS,
  );

  const resetFilters = () => {
    setPagination(DEFAULT_PAGINATION_STATE);
    setFilter(DEFAULT_OVERVIEW_FILTER);
    setSorting([]);
  };

  const [pagination, setPagination] = useState<QuestionnaireTablePagination>({
    pageIndex: 0,
    pageSize: DEFAULT_OVERVIEW_FILTER.rows_per_page,
  });

  useEffect(() => {
    const newPageIndex: number = pagination?.pageIndex ?? 0;

    if (newPageIndex !== filter.page) {
      setFilter({
        ...filter,
        page: newPageIndex,
      });
    }
  }, [pagination?.pageIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pagination?.pageSize !== filter.rows_per_page) {
      setFilter({
        ...filter,
        rows_per_page: pagination?.pageSize,
      });

      setCookie(OVERVIEW_PAGE_SIZE_COOKIE, pagination?.pageSize.toString(), {
        maxAge: MAX_COOKIE_AGE,
      });
    }
  }, [pagination?.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load cookies
  useEffect(() => {
    const readCookies = () => {
      let columns: Array<OverviewColumnType> = DEFAULT_OVERVIEW_COLUMNS;
      let filters: OverviewFilterObject = DEFAULT_OVERVIEW_FILTER;

      try {
        const overviewColumnsCookie: CookieValueTypes = getCookie(
          OVERVIEW_COLUMNS_COOKIE,
        );
        const overviewFilterCookie: CookieValueTypes = getCookie(
          OVERVIEW_FILTERS_COOKIE,
        );
        const overviewPageSizeCookie: CookieValueTypes = getCookie(
          OVERVIEW_PAGE_SIZE_COOKIE,
        );

        if (overviewColumnsCookie) {
          const parsed: Array<OverviewColumnType> = JSON.parse(
            overviewColumnsCookie as string,
          );

          // NOTE @chris make sure actions are always the latest
          if (parsed && Array.isArray(parsed)) {
            const filteredParsed: Array<OverviewColumnType> = parsed.filter(
              (p) => p !== 'actions',
            );
            filteredParsed.push('actions');

            columns = filteredParsed;
          }
        }

        if (overviewFilterCookie) {
          const parsed: OverviewFilterObject = JSON.parse(
            overviewFilterCookie as string,
          );

          if (parsed && !!parsed?.rows_per_page) {
            filters = parsed;
          }
        }

        if (overviewPageSizeCookie) {
          const parsed: number = parseInt(overviewPageSizeCookie as string);

          if (parsed && !isNaN(parsed)) {
            setPagination((prev) => ({
              ...prev,
              pageSize: parsed,
            }));
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setColumns(columns);
        setFilter({ ...filters, page: 0 });
      }
    };

    readCookies();
  }, []);

  useEffect(() => {
    const updateColumnsCookie = () => {
      try {
        const overviewColumnsCookie = JSON.stringify(columns);
        setCookie(OVERVIEW_COLUMNS_COOKIE, overviewColumnsCookie, {
          maxAge: MAX_COOKIE_AGE,
        });
      } catch (error) {
        console.error(error);
      }
    };

    if (!!columns) updateColumnsCookie();
  }, [columns]);

  useEffect(() => {
    const updateFiltersCookie = () => {
      try {
        const overviewFiltersCookie = JSON.stringify(filter);
        setCookie(OVERVIEW_FILTERS_COOKIE, overviewFiltersCookie, {
          maxAge: MAX_COOKIE_AGE,
        });
      } catch (error) {
        console.error(error);
      }
    };

    if (!!filter) updateFiltersCookie();
  }, [filter]);

  const isFiltering = useMemo(() => {
    const { firm_role_id: _0, rows_per_page: _2, ...cleanFilter } = filter;
    const {
      firm_role_id: _1,
      rows_per_page: _3,
      ...cleanDefaultFilter
    } = DEFAULT_OVERVIEW_FILTER;

    return !areSameObjects(cleanFilter, cleanDefaultFilter);
  }, [filter]);

  const resetColumns = () => {
    deleteCookie(OVERVIEW_COLUMNS_COOKIE);
    setColumns(DEFAULT_OVERVIEW_COLUMNS);
  };

  const onSetColumns = (columns: Array<OverviewColumnType>) => {
    const ordered: Array<OverviewColumnType> = columns.filter(
      (c) => c !== 'select' && c !== 'actions',
    );
    const newOrdered: Array<OverviewColumnType> = [
      'select',
      ...ordered,
      'actions',
    ];

    return setColumns(newOrdered);
  };

  return (
    <OverviewContext
      value={{
        isFiltering,
        filter,
        setSorting,
        sorting,
        pagination,
        setPagination,
        setColumns: onSetColumns,
        columns,
        setFilter,
        resetFilters,
        resetColumns,
      }}
    >
      {props.children}
    </OverviewContext>
  );
};

export { OverviewContext, OverviewProvider };
